/**
 * "Fill range…": pick the first and last day, with or without the sister plants
 * the people are shared with, and watch the server fill it day by day.
 */
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, LinearProgress, Switch } from "@mui/material";
import { addDays, format, parseISO } from "date-fns";
import React, { useCallback, useEffect, useState } from "react";

import { FillRange } from "../controller/use-fill-range";
import { lbb } from "../utils/tokens";

interface FillRangeDialogProps {
	open: boolean;
	onClose: () => void;
	fill: FillRange;
	/** The week on screen: the range opens on it. */
	weekStart: Date;
	plantName?: string;
}

const dateInput: React.CSSProperties = {
	font: "inherit",
	fontFamily: lbb.fontMono,
	fontSize: 13,
	padding: "6px 8px",
	border: lbb.borderHair,
	borderRadius: 4,
	background: lbb.paper,
	color: lbb.ink,
};

/** "2026-10-26 · 345" → "Mon 26 Oct". */
const dayOf = (current?: string | null): string => {
	const day = (current ?? "").split(" · ")[0];
	return /^\d{4}-\d{2}-\d{2}$/.test(day) ? format(parseISO(day), "EEE d MMM") : "";
};

const FillRangeDialog: React.FC<FillRangeDialogProps> = ({ open, onClose, fill, weekStart, plantName }) => {
	const [from, setFrom] = useState(format(weekStart, "yyyy-MM-dd"));
	const [to, setTo] = useState(format(addDays(weekStart, 27), "yyyy-MM-dd"));
	const [includeCluster, setIncludeCluster] = useState(true);
	useEffect(() => {
		if (open && !fill.running) {
			setFrom(format(weekStart, "yyyy-MM-dd"));
			setTo(format(addDays(weekStart, 27), "yyyy-MM-dd"));
		}
	}, [open, weekStart, fill.running]);

	const handleFrom = useCallback((event: React.ChangeEvent<HTMLInputElement>) => setFrom(event.target.value), []);
	const handleTo = useCallback((event: React.ChangeEvent<HTMLInputElement>) => setTo(event.target.value), []);
	const handleCluster = useCallback((event: React.ChangeEvent<HTMLInputElement>) => setIncludeCluster(event.target.checked), []);
	const handleStart = useCallback(() => void fill.start(from, to, includeCluster), [fill, from, to, includeCluster]);

	const { progress } = fill;
	const days = from && to && to >= from ? Math.round((parseISO(to).getTime() - parseISO(from).getTime()) / 86400000) + 1 : 0;
	const percent = progress && progress.total > 0 ? Math.round((progress.done / progress.total) * 100) : 0;
	const finished = progress && progress.status !== "running";

	return (
		<Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
			<DialogTitle sx={{ fontFamily: lbb.fontBrand, color: lbb.navy }}>Fill a range of days</DialogTitle>
			<DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, fontFamily: lbb.fontUi, fontSize: 14, color: lbb.ink }}>
				{!fill.running && (
					<>
						<div style={{ display: "flex", alignItems: "center", gap: 8 }}>
							<input type="date" value={from} onChange={handleFrom} aria-label="First day" style={dateInput} />
							<span style={{ color: lbb.ink60 }}>to</span>
							<input type="date" value={to} min={from} onChange={handleTo} aria-label="Last day" style={dateInput} />
						</div>
						<FormControlLabel
							control={<Switch size="small" checked={includeCluster} onChange={handleCluster} />}
							label="Fill the plants that share people too"
							sx={{ "& .MuiFormControlLabel-label": { fontFamily: lbb.fontUi, fontSize: 13 } }}
						/>
						<span style={{ fontSize: 13, color: lbb.ink60 }}>
							Refills {days} day{days === 1 ? "" : "s"}{plantName ? ` from ${plantName}` : ""}, day by day. People you placed or pinned stay where they are. It runs on the server, so you can close this and keep working.
						</span>
					</>
				)}
				{progress && (
					<div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
						<LinearProgress variant="determinate" value={percent} sx={{ height: 6, borderRadius: 3, bgcolor: lbb.alloy, "& .MuiLinearProgress-bar": { bgcolor: lbb.navy } }} />
						<span style={{ fontSize: 13 }}>
							{progress.status === "running"
								? `Filling ${dayOf(progress.current)} — ${progress.done} of ${progress.total} plant-days`
								: progress.status === "done"
									? `Done: ${progress.total} plant-days, ${progress.assigned} people placed.`
									: "The fill stopped before the end."}
						</span>
						{progress.errors.length > 0 && (
							<span style={{ fontSize: 12, color: lbb.brickDeep }}>
								{progress.errors.length} day{progress.errors.length === 1 ? "" : "s"} could not be filled: {progress.errors.slice(0, 3).join("; ")}
							</span>
						)}
					</div>
				)}
				{fill.error && <span style={{ fontSize: 13, color: lbb.brickDeep }}>{fill.error}</span>}
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose} sx={{ textTransform: "none", color: lbb.ink }}>
					{fill.running ? "Keep running in the background" : "Close"}
				</Button>
				{!fill.running && (
					<Button
						onClick={handleStart}
						disabled={fill.starting || days < 1}
						variant="contained"
						sx={{ textTransform: "none", bgcolor: lbb.brick, "&:hover": { bgcolor: lbb.brickDeep } }}
					>
						{fill.starting ? "Starting…" : finished ? "Fill again" : "Fill"}
					</Button>
				)}
			</DialogActions>
		</Dialog>
	);
};

export default FillRangeDialog;
